/**
 * ProducerEventCard
 * Canonical card for Producer Portal work-list.
 * Shows booking stats (booked_spots/max_spots/is_full).
 * Includes visibility toggle and actions menu.
 *
 * Must not be used on public pages — use PublicEventCard instead.
 */
import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Calendar, MapPin, Eye, EyeOff, Clock, AlertTriangle, Pencil, Users, Copy } from 'lucide-react';
import { ShareStudioSheet } from '@/features/share/ShareStudioSheet';
import { features } from '@/config/features';
import { formatDistanceToNow } from 'date-fns';
import { sv } from 'date-fns/locale';
import { useCoverImageUrl } from '@/hooks/useMediaAssets';
import { getActivityEditHref } from '@/lib/activities/routes';
import { EventActionsMenu } from '@/components/events/EventActionsMenu';
import { EventTypeBadge } from '@/components/events/EventTypeBadge';
import { getEventTimeBucket } from '@/lib/events/timeBucket';
import { formatEventTimingSummary } from '@/lib/events/eventCardUtils';
import { cn } from '@/lib/utils';
import type { ActivityType } from '@/types/activity';
import type { EventType } from '@/types/events';

// ─── Types ──────────────────────────────────────────────────────────

export interface ProducerEventCardItem {
  id: string;
  title: string;
  activityType: ActivityType;
  eventTypeRaw: string;
  status: string;
  startDate: string;
  endDate: string;
  timingMode: 'date_time' | 'open' | 'anytime' | 'season';
  
  selectedSeasons: string[];
  /** @deprecated — no longer used; preview derived from description */
  shortDescription: string | null;
  updatedAt: string;
  tags: string[];
  useProducerAddress: boolean;
  customAddress: string | null;
  maxSpots: number;
  bookedSpots: number;
  isFull: boolean;
  spotsLeft: number | null;
  bookingMode?: 'none' | 'optional' | 'required';
  occurrencesCount?: number;
  /** Canonical price fields for Share Studio */
  isFree: boolean;
  priceOre: number;
}

export type ProducerEventCardVariant = 'default' | 'compact';

export interface ProducerEventCardProps {
  item: ProducerEventCardItem;
  farmId: string;
  hostName: string;
  producerSlug?: string;
  producerIsPublic?: boolean;
  isPast?: boolean;
  isToggling?: boolean;
  /** Visual variant: 'compact' reduces image size and visual weight for past/archival items */
  variant?: ProducerEventCardVariant;
  onToggleVisibility: () => void;
  onDelete: () => void;
  onClone?: () => void;
}

// ─── Constants ──────────────────────────────────────────────────────

const TAG_LABELS: Record<string, string> = {
  barnvanlig: 'Barnvänlig',
  plocka_sjalv: 'Plocka själv',
  kultur: 'Kultur',
};

const STATUS_CONFIG = {
  public: { label: 'Publicerad', variant: 'default' as const, icon: Eye },
  draft: { label: 'Utkast', variant: 'secondary' as const, icon: Clock },
  cancelled: { label: 'Dold', variant: 'outline' as const, icon: EyeOff },
} as const;

// ─── Component ──────────────────────────────────────────────────────

export function ProducerEventCard({
  item,
  farmId,
  hostName,
  producerSlug,
  producerIsPublic = true,
  isPast = false,
  isToggling,
  variant = 'default',
  onToggleVisibility,
  onDelete,
  onClone,
}: ProducerEventCardProps) {
  const isCompact = variant === 'compact';
  const navigate = useNavigate();
  const routable = { id: item.id, activityType: item.activityType };
  const editHref = getActivityEditHref(farmId, routable);
  const [showShareStudio, setShowShareStudio] = useState(false);
  const coverUrl = useCoverImageUrl('event', item.id, 640);

  const statusCfg = STATUS_CONFIG[item.status as keyof typeof STATUS_CONFIG] || STATUS_CONFIG.draft;
  const StatusIcon = statusCfg.icon;

  const showTimeStatus = item.timingMode === 'date_time';
  const timeBucket = showTimeStatus
    ? getEventTimeBucket({ startDate: item.startDate, endDate: item.endDate })
    : null;

  const seasonOutingTags = item.selectedSeasons.map((season) => `season:${season}`);
  const timingSummary = formatEventTimingSummary(item.timingMode, item.startDate, item.endDate, seasonOutingTags);

  const visibleTags = item.tags.slice(0, 2);
  const extraTags = item.tags.length - 2;

  const isPublishable = !!(item.title && item.startDate && item.endDate) && producerIsPublic;

  return (
    <>
    <div
      data-testid="activity-card"
      onClick={() => navigate(editHref)}
      className={cn(
        'group relative flex flex-col rounded-2xl border border-border bg-card overflow-hidden cursor-pointer',
        'transition-all duration-200 hover:shadow-md hover:-translate-y-0.5',
        isCompact && 'opacity-70',
        isPast && !isCompact && 'opacity-60'
      )}
    >
      {/* Cover image */}
      <div className={cn(
        'relative bg-muted/40 overflow-hidden',
        isCompact ? 'aspect-[3/1]' : 'aspect-[16/9]',
      )}>
        <ProducerEventCoverImage itemId={item.id} title={item.title} />
        {isCompact && <div className="absolute inset-0 bg-foreground/5" />}
        <ProducerEventCoverImage itemId={item.id} title={item.title} />
        {/* Badges overlay */}
        <div className="absolute top-2 left-2 flex gap-1.5">
          <Badge variant={statusCfg.variant} className="text-xs gap-1 shadow-sm" data-testid="activity-badge-status">
            <StatusIcon className="h-3 w-3" />
            {statusCfg.label}
          </Badge>
          {timeBucket && (
            <Badge
              variant={timeBucket.bucket === 'past' ? 'secondary' : 'default'}
              className="text-xs gap-1 shadow-sm"
            >
              {timeBucket.label}
            </Badge>
          )}
          {item.eventTypeRaw === 'course' && (
            <EventTypeBadge type={item.eventTypeRaw as EventType} overlay />
          )}
        </div>
        {/* ⋯ menu overlay */}
        <div className="absolute top-2 right-2" onClick={(e) => e.stopPropagation()}>
          <EventActionsMenu
            isPublished={item.status === 'public'}
            isPast={isPast}
            isToggling={isToggling}
            onEdit={() => navigate(editHref)}
            onToggleVisibility={onToggleVisibility}
            onDelete={onDelete}
            onClone={onClone}
          />
        </div>
      </div>

      {/* Content */}
      <div className="flex flex-col flex-1 p-4 gap-2">
        <h3 className="font-semibold text-foreground line-clamp-2 leading-snug text-sm">
          {item.title || 'Namnlös aktivitet'}
        </h3>

        {/* Date */}
        <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
          <Calendar className="h-3 w-3 shrink-0" />
          <span>{timingSummary}</span>
        </div>

        {/* Occurrences count for series */}
        {(item.occurrencesCount ?? 0) > 1 && (
          <p className="text-xs text-muted-foreground ml-[1.125rem]">
            {item.occurrencesCount === 1 ? '1 tillfälle' : `${item.occurrencesCount} tillfällen`}
          </p>
        )}

        {/* Date error: end before start */}
        {item.startDate && item.endDate && new Date(item.endDate) < new Date(item.startDate) && (
          <p className="text-[10px] text-destructive font-medium">
            ⚠ Slutdatum är före startdatum.
          </p>
        )}

        {/* Location */}
        <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
          <MapPin className="h-3 w-3 shrink-0" />
          <span>
           {item.useProducerAddress
              ? `Hos ${hostName}`
              : item.customAddress
              ? item.customAddress
              : 'Plats ej vald'}
          </span>
        </div>

        {/* Booking stats – only shown when booking_mode !== 'none' */}
        {item.activityType !== 'excursion' && item.bookingMode && item.bookingMode !== 'none' && (
          <div
            className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-primary transition-colors cursor-pointer"
            onClick={(e) => {
              e.stopPropagation();
              navigate(`/app/producer/farm/${farmId}/events/${item.id}/bookings`);
            }}
            role="link"
            title={item.bookingMode === 'optional' ? 'Visa intressen' : 'Visa bokningar'}
          >
            <Users className="h-3 w-3 shrink-0" />
            <span className="underline decoration-dotted underline-offset-2">
              {item.bookingMode === 'optional'
                ? `${item.bookedSpots} intresse${item.bookedSpots !== 1 ? 'n' : ''}`
                : item.maxSpots
                  ? `${item.bookedSpots} / ${item.maxSpots} bokade`
                  : `${item.bookedSpots} bokade`}
            </span>
            {item.isFull && (
              <Badge variant="destructive" className="text-[10px] px-1.5 py-0 ml-1">Fullbokat</Badge>
            )}
          </div>
        )}

        {/* Tags */}
        {item.tags.length > 0 && (
          <div className="flex gap-1 flex-wrap mt-0.5">
            {visibleTags.map((t) => (
              <Badge key={t} variant="outline" className="text-[10px] px-1.5 py-0">
                {TAG_LABELS[t] || t}
              </Badge>
            ))}
            {extraTags > 0 && (
              <Badge variant="outline" className="text-[10px] px-1.5 py-0">
                +{extraTags}
              </Badge>
            )}
          </div>
        )}

        {/* Updated */}
        <p className="text-[10px] text-muted-foreground/70 mt-auto pt-1">
          Uppdaterad {formatDistanceToNow(new Date(item.updatedAt), { addSuffix: true, locale: sv })}
        </p>
      </div>

      {/* Publish-gate hint when producer is not public */}
      {!producerIsPublic && item.status !== 'public' && (
        <div className="px-4 py-2 border-t border-border bg-muted/30" data-testid="publish-gate-hint">
          <p className="text-[11px] text-muted-foreground leading-snug">
            Gör gården synlig för att kunna publicera aktiviteter.
          </p>
          <Link
            to={`/app/producer/farm/${farmId}`}
            className="text-[11px] font-medium text-primary underline underline-offset-2 hover:text-primary/80"
            data-testid="publish-gate-cta"
            onClick={(e) => e.stopPropagation()}
          >
            Gör synlig →
          </Link>
        </div>
      )}

      {/* Actions footer */}
      <div className="flex items-center gap-1.5 border-t border-border px-3 py-2" onClick={(e) => e.stopPropagation()}>
        {isPast ? (
          <>
            <Button variant="ghost" size="sm" className="text-xs h-7 gap-1" onClick={() => navigate(editHref)} data-testid="activity-action-view">
              <Eye className="h-3 w-3" />
              Visa
            </Button>
            {onClone && (
              <Button variant={isCompact ? 'ghost' : 'default'} size="sm" className="text-xs h-7 gap-1 ml-auto" onClick={onClone} data-testid="activity-action-clone">
                <Copy className="h-3 w-3" />
                {isCompact ? 'Kopiera' : 'Skapa ny från denna'}
              </Button>
            )}
          </>
        ) : (
          <>
            <Button size="sm" className="text-xs h-7 gap-1" onClick={() => navigate(editHref)} data-testid="activity-action-edit">
              <Pencil className="h-3 w-3" />
              Redigera
            </Button>
            <Button variant="ghost" size="sm" className="text-xs h-7 gap-1" data-testid="activity-action-view" onClick={() => {
              window.open(`/event/${item.id}`, '_blank');
            }}>
              <Eye className="h-3 w-3" />
              Visa
            </Button>
            <div className="flex-1" />
            <EventActionsMenu
              isPublished={item.status === 'public'}
              isToggling={isToggling}
              onEdit={() => navigate(editHref)}
              onToggleVisibility={onToggleVisibility}
              onDelete={onDelete}
              onClone={onClone}
              onShare={features.enableShareStudio && item.status === 'public' ? () => setShowShareStudio(true) : undefined}
              showPublishAction
              isPublishable={isPublishable}
            />
          </>
        )}
      </div>

    </div>

    {/* Share Studio sheet — rendered outside the clickable card to prevent navigate-on-close */}
    {features.enableShareStudio && showShareStudio && (
      <ShareStudioSheet
        open={showShareStudio}
        onOpenChange={setShowShareStudio}
        target={{
          type: 'activity',
          id: item.id,
          slug: null,
          title: item.title,
          heroImageUrl: coverUrl || null,
          producerName: hostName,
          producerSlug: producerSlug || '',
          startDate: item.startDate,
          endDate: item.endDate,
          timingMode: item.timingMode,
          isFree: item.isFree,
          priceOre: item.priceOre,
          bookingMode: item.bookingMode || 'none',
          venueText: item.customAddress || null,
          descriptionHtml: null,
        }}
      />
    )}
    </>
  );
}

// ─── Helper: cover image from media_assets ──────────────────────────
function ProducerEventCoverImage({ itemId, title }: { itemId: string; title: string }) {
  const url = useCoverImageUrl('event', itemId, 320);
  
  if (url) {
    return (
      <img
        src={url}
        alt={title}
        className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-[1.02]"
      />
    );
  }
  return (
    <div className="flex items-center justify-center w-full h-full">
      <span className="text-3xl opacity-30">🌾</span>
    </div>
  );
}
